import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DataTable,
  DataTableColumnSettings,
  DataTableEmpty,
  DataTableGroupByDropdown,
  DataTableToolbar,
  DataTableToolbarLeft,
  DataTableToolbarActions,
  useDataTableState,
  renderDefaultGroupActions,
  resolveVisibleGroups,
  UNASSIGNED_GROUP_KEY,
  Box,
  Button,
  MenuItem,
  Text,
  Icon,
  Separator,
  toastManager,
  ToolbarAction,
  type BulkAction,
  type DataTableGroupActionsContext,
  type DataTableGroupType,
  type SelectAllGroup,
  omitUndefined,
} from "@salestrak/ui";
import { FilterToggle } from "#app/components/filters/filter-toggle";

import {
  IssueListDataDocument,
  type IssueRow,
  IssuesDocument,
  type StageOption,
} from "#app/lib/data";
import { useListQueryState } from "#app/lib/hooks/use-list-query-state";
import { ISSUE_LIST } from "#app/lib/filters/list-configs";
import { useTableFilters, type FilterDef } from "#app/lib/hooks/use-table-filters";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { IssueMenuItemsWithActions } from "#app/components/issue-actions/issue-menu-items";
import { useItemShareDialogActions } from "#app/lib/item-share-dialog-context";
import { canEdit, canManageSharing } from "#app/lib/utils/access-level";
import { RenameIssueDialog } from "#app/components/issue-actions/rename-issue-dialog";
import { useIssueCardActions } from "#app/hooks/kanban/use-issue-card-actions";
import { useDeleteIssueWithUndo } from "#app/hooks/kanban/use-delete-issue-with-undo";
import { EntityTag } from "#app/components/entity";
import { ENTITY_KIND_ICON, iconSourceToName } from "#app/lib/entity-visuals";
import { AGE_STATUS_CONFIG, type AgeStatus } from "#app/lib/age-status";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import {
  StageSelector,
  AssigneeSelector,
  useUserPool,
  useTeamPool,
} from "#app/components/entity-selectors";
import { UpdateAgeDialog } from "#app/components/age/update-age-dialog";
import { useIssueColumns } from "#app/components/issues/issue-columns";
import {
  issueCustomFieldColumnId,
  useIssueCustomFieldColumns,
} from "#app/components/issues/issue-custom-field-columns";
import { useCustomFieldValueCommit } from "#app/hooks/issue-view/use-custom-field-value-commit";
import { useWorkspaceCustomFields } from "#app/hooks/use-workspace-custom-fields";
import {
  BulkAssigneeContent,
  BulkStageContent,
  buildSelectAllWhere,
  type BulkIssueMutations,
} from "#app/components/issues/issue-bulk-actions";
import {
  buildGroupWhere,
  ISSUE_GROUP_SERVER_KEY,
  AGE_GROUPS,
  UNASSIGNED_DIMENSION_LABEL,
  type IssueGroupDimension,
} from "#app/components/issues/issue-grouping";
import { useIssueViewMutations } from "#app/hooks/issue-view/use-issue-view-mutations";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import { useRefetchIssueGroups } from "#app/hooks/issue-list/use-refetch-issue-groups";
import { useIssueListMutations } from "#app/hooks/issue-list/use-issue-list-mutations";
import { issueListSubscriptions } from "#app/hooks/issue-list/issue-list-subscriptions";
import { useRegisterSubscriptions } from "#app/lib/subscriptions/subscription-provider";
import { useIssueView } from "#app/components/issue-view";
import { useCurrentUserFilterToggle } from "#app/lib/hooks/use-current-user-filter-toggle";
import { useWorkflowStages } from "#app/lib/hooks/use-workflow-stages";
import { useIssueTypes } from "#app/lib/hooks/use-issue-types";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useAppData } from "#app/lib/data";
import {
  scopeWhere,
  scopeWorkflowId,
  scopeKey,
  type IssueScope,
} from "#app/lib/filters/issue-scope";

import { useIssueFilters } from "#app/lib/filters/issue-filter-context";
import { useAutomateStage } from "#app/hooks/automations/use-automate-stage";
import { AutomateStageMenuItem } from "#app/components/automations/automate-stage-menu-item";
import {
  useAnalytics,
  AnalyticsContext,
  IssueEvent,
  AnalyticsView,
  BulkOperation,
  BulkMode,
  useTrackSearch,
} from "#app/lib/analytics";
import { CurrentUserButton } from "#app/components/filters/current-user-button";
import {
  applyCurrentUserFilter,
  combineWhereConditions,
} from "#app/lib/query-utils";
import {
  GroupQueryProvider,
  useGroupStates,
} from "#app/lib/hooks/group-query-provider";
import { ExpandableSearch } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

/**
 * The one issue table, for any scope. A single workflow groups by stage and can
 * create projects + bulk-move stages; a team or member spans many workflows, so
 * it defaults to a flat list (stage is per-row editable, but no stage grouping,
 * no bulk-stage, no create). Everything workflow-specific is gated on
 * `scopeWorkflowId(scope)`. Self-contained — the route renders `<IssuesTable
 * scope=… />` and this owns its own data/filters/mutations via the shared
 * issue data layer (`GroupQueryProvider` + `useGroupStates`) and an ambient
 * `IssueFilterProvider` for panel filters.
 */

// Shared by the table (sticky pinning) and the column settings panel (locked
// rows). The panel ignores structural ids like "select".
const ISSUES_PINNED_COLUMN_IDS = ["select", "name"];

// ---------------------------------------------------------------------------
// Per-group query helpers
// ---------------------------------------------------------------------------

const getConnection = (data: any) => data?.issues;

// The table's group field keys differ from the shared grouping module's
// dimension names (`assignees` vs `assignee`); map them so the table and board
// resolve selectors + server group keys through the one shared source.
const FIELD_TO_DIMENSION: Record<string, IssueGroupDimension> = {
  stage: "stage",
  assignees: "assignee",
  team: "team",
  age: "age",
  issueType: "issueType",
};

function groupWhereForField(
  field: string,
  key: string,
): Record<string, unknown> | undefined {
  const dimension = FIELD_TO_DIMENSION[field];
  return dimension ? buildGroupWhere(dimension, key) : undefined;
}

function serverGroupKeyForField(field: string): string | undefined {
  const dimension = FIELD_TO_DIMENSION[field];
  return dimension ? ISSUE_GROUP_SERVER_KEY[dimension] : undefined;
}


// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function IssuesTable({ scope }: { scope: IssueScope }) {
  const workflowId = scopeWorkflowId(scope);
  // List-level realtime is workflow-keyed; inert for cross-workflow scopes.
  // Shares wire subscriptions with a mounted board on the same workflow.
  useRegisterSubscriptions(
    workflowId ? issueListSubscriptions(workflowId) : [],
  );
  const { track } = useAnalytics();

  // --- Data wiring (self-contained, driven by scope) ---
  const { openIssue, openCreateIssue } = useIssueView();
  const { changeStage, updateAssignees, bulk } = useIssueListMutations();
  const bulkMutations: BulkIssueMutations = bulk;
  const refetchGroups = useRefetchIssueGroups();

  const { pool: userPool } = useUserPool();
  const { pool: teamPool } = useTeamPool();
  const { workflows } = useAppData();

  const {
    isCurrentUserButtonSelected,
    isCurrentUserFilterApplied,
    handleCurrentUserPress,
    handleCurrentUserClear,
  } = useCurrentUserFilterToggle();

  // Stages: a single workflow uses that workflow's ordered stages; a
  // cross-workflow scope resolves labels from every workspace stage so the
  // Stage column never renders "Unknown" for a row in another workflow.
  const { stages: workflowStages } = useWorkflowStages(workflowId);
  const { stages: workspaceStages } = useWorkspaceData();
  const availableStages = useMemo<StageOption[]>(
    () => (workflowId ? workflowStages : workspaceStages),
    [workflowId, workflowStages, workspaceStages],
  );

  const issueTypes = useIssueTypes();

  const handleOpenIssue = useCallback(
    (issue: IssueRow) => openIssue(issue),
    [openIssue],
  );

  // --- Filters ---
  const filterDefs = useMemo<FilterDef[]>(
    () => [
      {
        key: "stageId",
        label: "Stage",
        icon: "layers",
        type: "select",
        operator: "in",
        options: availableStages.map((s) => ({
          value: s.stageId,
          label: s.name,
        })),
      },
      {
        key: "assignees",
        label: "Assignees",
        icon: "users",
        type: "entity",
        filterFormat: "entityRef",
        operator: "hasSome",
        pools: [userPool],
        placeholder: "Search members...",
      },
      {
        key: "assigneeTeamIds",
        label: "Team",
        icon: ENTITY_KIND_ICON.team,
        type: "entity",
        operator: "hasSome",
        pools: [teamPool],
        placeholder: "Search teams...",
      },
    ],
    [availableStages, userPool, teamPool],
  );

  const tableFilters = useTableFilters(filterDefs);
  const { toggleFilterPanel, filters: panelFilters } = useIssueFilters();


  const [ageDialogIssue, setAgeDialogIssue] = useState<IssueRow | null>(null);
  const handleAgePress = useCallback((issue: IssueRow) => setAgeDialogIssue(issue), []);
  const handleAgeDialogClose = useCallback((open: boolean) => {
    if (!open) setAgeDialogIssue(null);
  }, []);

  const { updateAddress } = useIssueViewMutations();

  const handleAddressSelect = useCallback(
    (issue: IssueRow, wholeAddress: string) => {
      updateAddress({
        variables: { issueId: issue.issueId, address: { wholeAddress } },
        optimisticResponse: {
          // `omitUndefined` because the optimistic payload must match the
          // schema shape: an issue with no address DELETES `addressId` rather
          // than caching an explicit `undefined`.
          updateIssue: omitUndefined({
            __typename: "Issue" as const,
            id: issue.id,
            issueId: issue.issueId,
            addressId: issue.addressId,
            address: {
              __typename: "Address" as const,
              id: issue.address?.id ?? `optimistic-${issue.issueId}`,
              addressId: issue.address?.addressId ?? `optimistic-${issue.issueId}`,
              wholeAddress,
            },
          }),
        },
        onCompleted: (data) => {
          const result = data.updateIssue;
          if (result.__typename === "Issue") {
            toastManager.add({ title: "Address updated", type: "success" });
          } else {
            toastManager.add({ title: "message" in result ? result.message : "Failed to update address", type: "error" });
          }
        },
        onError: (err) => toastMutationError(err, "Failed to update address"),
      });
    },
    [updateAddress],
  );

  const handleAddressClear = useCallback(
    (issue: IssueRow) => {
      updateAddress({
        variables: { issueId: issue.issueId, address: null },
        optimisticResponse: {
          updateIssue: {
            __typename: "Issue" as const,
            id: issue.id,
            issueId: issue.issueId,
            addressId: null,
            address: null,
          },
        },
        onCompleted: (data) => {
          const result = data.updateIssue;
          if (result.__typename === "Issue") {
            toastManager.add({ title: "Address cleared", type: "success" });
          } else {
            toastManager.add({ title: "message" in result ? result.message : "Failed to clear address", type: "error" });
          }
        },
        onError: (err) => toastMutationError(err, "Failed to clear address"),
      });
    },
    [updateAddress],
  );

  // --- Columns ---
  const rawColumns = useIssueColumns({
    availableStages,
    workflows,
    onOpenIssue: handleOpenIssue,
    onChangeStage: changeStage,
    onAssigneesChange: updateAssignees,
    onAgePress: handleAgePress,
    onAddressSelect: handleAddressSelect,
    onAddressClear: handleAddressClear,
  });

  // --- Custom-field columns ---
  // Every workspace field is an opt-in column; cells reuse the issue view
  // dialog's value cell. Hidden by default via `defaultHidden` below.
  const customFields = useWorkspaceCustomFields();
  const commitCustomFieldValue = useCustomFieldValueCommit();
  const customFieldColumns = useIssueCustomFieldColumns({
    fields: customFields,
    commit: commitCustomFieldValue,
  });
  const allColumns = useMemo(
    () => [...rawColumns, ...customFieldColumns],
    [rawColumns, customFieldColumns],
  );
  const customFieldColumnIds = useMemo(
    () => customFields.map((f) => issueCustomFieldColumnId(f.customFieldId)),
    [customFields],
  );

  // Group buckets: assignee is users-only; teams are their own dimension.
  const userGroups = useMemo(() => {
    const groups = userPool.items.map((item) => ({
      key: item.id,
      label: item.title,
    }));
    groups.sort((a, b) => a.label.localeCompare(b.label));
    return groups;
  }, [userPool.items]);

  const teamGroups = useMemo(() => {
    const groups = teamPool.items.map((item) => ({
      key: item.id,
      label: item.title,
    }));
    groups.sort((a, b) => a.label.localeCompare(b.label));
    return groups;
  }, [teamPool.items]);

  // --- Per-user column customization (order / hidden / shown / widths) ---
  const tableView = useTableViewSettings("issues", {
    defaultHidden: customFieldColumnIds,
  });

  // --- Data table state (selection, grouping) ---
  // Stage grouping needs a single workflow; cross-workflow scopes default to a
  // flat list and omit stage from the picker (server count grouping by
  // workflow isn't supported, so workflow stays a board-only dimension).
  const {
    columns,
    groupByField,
    setGroupByField,
    isGrouped,
    groupByLabels,
    groupByIcons,
    activeGroupConfig,
    groupSortDirection,
    setGroupSortDirection,
  } = useDataTableState({
    columns: allColumns,
    defaultGroupByField: workflowId ? "stage" : "none",
    defaultGroupSortDirection: "asc",
    groupFields: {
      ...(workflowId
        ? {
            stage: {
              label: "Stage",
              icon: "circle" as const,
              groups: [
                ...availableStages.map((s) => ({ key: s.stageId, label: s.name })),
                { key: UNASSIGNED_GROUP_KEY, label: "No stage" },
              ],
              filterKey: "stageId",
            },
          }
        : {}),
      assignees: {
        label: "Assignee",
        icon: "users",
        groups: [
          ...userGroups,
          { key: UNASSIGNED_GROUP_KEY, label: "No assignee" },
        ],
        filterKey: "assignees",
      },
      team: {
        label: "Team",
        icon: ENTITY_KIND_ICON.team,
        groups: [
          ...teamGroups,
          { key: UNASSIGNED_GROUP_KEY, label: "No team" },
        ],
        filterKey: "assigneeTeamIds",
      },
      age: {
        label: "Age",
        icon: "clock",
        groups: [...AGE_GROUPS],
        preserveOrder: true,
      },
      issueType: {
        label: "Project Type",
        icon: ENTITY_KIND_ICON.issueType,
        groups: [
          ...issueTypes.map((t) => ({ key: t.issueTypeId, label: t.name })),
          { key: UNASSIGNED_GROUP_KEY, label: "No type" },
        ],
        filterKey: "issueTypeId",
      },
    },
  });

  // Combine panel-level filters with table-level filters and the "Only Me"
  // toggle (current-user only when ungrouped — grouped queries fold it per
  // group via getGroupWhere instead).
  const filterWhere = useMemo(() => {
    const conditions: Record<string, unknown>[] = [];
    if (panelFilters.where) conditions.push(panelFilters.where);
    if (tableFilters.where) conditions.push(tableFilters.where);
    const combined = combineWhereConditions(conditions);
    return applyCurrentUserFilter(
      combined,
      isCurrentUserFilterApplied && !isGrouped,
    );
  }, [panelFilters.where, tableFilters.where, isCurrentUserFilterApplied, isGrouped]);

  // Scope + "not archived" are the table's own predicate, not the user's — they
  // lead the AND fold ahead of search and filters. `groupBaseWhere` below
  // repeats them because grouped queries are built from that base instead.
  const issueBaseWhere = useMemo(
    () => [scopeWhere(scope), { archived: { eq: false } }],
    [scope],
  );

  const {
    searchValue,
    setSearchValue,
    sorting,
    setSorting,
    searchWhere,
    orderBy,
    where,
  } = useListQueryState(ISSUE_LIST, { baseWhere: issueBaseWhere, filterWhere });
  useTrackSearch(searchValue, () => ({
    context: AnalyticsContext.Issue,
    event: IssueEvent.Search,
    data: { view: AnalyticsView.List, scope: scope.kind },
  }));

  const [renameTarget, setRenameTarget] = useState<IssueRow | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const { renameIssue } = useIssueCardActions();
  const deleteWithUndo = useDeleteIssueWithUndo();
  const { openDialog: openShareDialog } = useItemShareDialogActions();

  const renderRowActions = useCallback(
    (issue: IssueRow) => (
      <IssueMenuItemsWithActions
        issue={issue}
        prepend={
          <MenuItem onClick={() => handleOpenIssue(issue)}>
            <Icon name="external-link" size="lg" color="muted" />
            <Text size="sm">Open project</Text>
          </MenuItem>
        }
        onRename={() => { setRenameValue(issue.name); setRenameTarget(issue); }}
        onDelete={() => { void deleteWithUndo(issue); }}
        onShare={() => openShareDialog(issue.issueId)}
        canManageSharing={canManageSharing(issue.viewerAccessLevel)}
        canDelete={!!issue.viewerCanDelete}
        canArchive={!!issue.viewerCanArchive}
      />
    ),
    [handleOpenIssue, deleteWithUndo, openShareDialog],
  );

  // --- Group query state ---
  const { states, registerState, getGroupProps } = useGroupStates<IssueRow>();

  // Track which groups have been activated by the DataTable's lazy loading.
  // When onGroupActivate fires, we add the key here to mount a GroupQueryProvider.
  const [activatedKeys, setActivatedKeys] = useState<Set<string>>(new Set());
  const handleGroupActivate = useCallback((key: string) => {
    setActivatedKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  // Reset activated keys when the group-by field changes
  const prevGroupByFieldRef = useRef(groupByField);
  if (prevGroupByFieldRef.current !== groupByField) {
    prevGroupByFieldRef.current = groupByField;
    setActivatedKeys(new Set());
  }

  const visibleGroups = useMemo(() => {
    if (!isGrouped || !activeGroupConfig) return [];
    return resolveVisibleGroups(
      activeGroupConfig,
      tableFilters.filters,
      groupSortDirection,
    );
  }, [isGrouped, activeGroupConfig, tableFilters.filters, groupSortDirection]);

  const groupBaseWhere = useMemo(() => {
    const effectiveTableWhere =
      isGrouped && activeGroupConfig?.filterKey
        ? tableFilters.getWhereExcluding(activeGroupConfig.filterKey)
        : tableFilters.where;
    // Scope + archived scoping must be repeated here: the ungrouped `where`
    // carries them, but grouped queries are built from this base instead.
    // Without them, group wheres like "assignee hasSome [x]" match issues
    // outside the scope, producing rows whose stages aren't in availableStages
    // (rendered as "Unknown").
    const conditions: Record<string, unknown>[] = [
      scopeWhere(scope),
      { archived: { eq: false } },
    ];
    if (searchWhere) conditions.push(searchWhere);
    if (effectiveTableWhere) conditions.push(effectiveTableWhere);
    if (panelFilters.where) conditions.push(panelFilters.where);
    return combineWhereConditions(conditions);
  }, [scope, isGrouped, activeGroupConfig, searchWhere, tableFilters, panelFilters.where]);

  // --- Single query for all group totals ---
  // Use groupBaseWhere (group field filter stripped) + currentUser filter
  // so counts match the per-group data queries.
  const groupCountsDbField = serverGroupKeyForField(groupByField);
  const groupCountsWhere = useMemo(() => {
    if (!isGrouped || !groupCountsDbField) return undefined;
    return applyCurrentUserFilter(
      groupBaseWhere,
      isCurrentUserFilterApplied ?? false,
    );
  }, [
    isGrouped,
    groupCountsDbField,
    groupBaseWhere,
    isCurrentUserFilterApplied,
  ]);
  const { counts: groupCountsRecord, ready: groupCountsReady } =
    useIssueGroupCounts({
      where: groupCountsWhere,
      groupBy: groupCountsDbField,
    });

  // --- getGroupWhere: resolves a group key to its full where clause ---
  const getGroupWhere = useCallback(
    (groupKey: string): Record<string, unknown> => {
      if (!isGrouped || groupKey === "__all__") {
        return where;
      }
      return applyCurrentUserFilter(
        combineWhereConditions(
          [groupBaseWhere, groupWhereForField(groupByField, groupKey)].filter(
            Boolean,
          ) as Record<string, unknown>[],
        ),
        isCurrentUserFilterApplied ?? false,
      );
    },
    [
      isGrouped,
      where,
      groupBaseWhere,
      groupByField,
      isCurrentUserFilterApplied,
    ],
  );

  // --- Bulk actions ---
  // Declared after `getGroupWhere`, which its content closures call: the
  // memo cannot list a binding declared below it.
  // Bulk change-stage only for a single workflow (a cross-workflow selection
  // has no single stage target). Bulk assignees works for any scope.
  const bulkActions = useMemo<BulkAction<IssueRow>[]>(() => {
    const actions: BulkAction<IssueRow>[] = [];

    const wfId = workflowId;
    if (wfId && availableStages.length > 0) {
      actions.push({
        id: "change-stage",
        type: "popover",
        label: "Move to stage",
        icon: "layers",
        contentProps: { padding: "none" },
        content: ({ selectedRows, selectAllGroups, commit }) => {
          const selectAllWhere = buildSelectAllWhere(
            selectAllGroups,
            getGroupWhere,
          );
          return (
            <BulkStageContent
              view={AnalyticsView.List}
              scope={scope.kind}
              workflowId={selectedRows[0]?.workflowId ?? wfId}
              commit={commit}
              bulkMutations={bulkMutations}
              refetchGroups={refetchGroups}
              availableStages={availableStages}
              selectedCount={
                selectAllGroups.length > 0
                  ? selectAllGroups.reduce((s, g) => s + g.totalCount, 0)
                  : selectedRows.length
              }
              selectAllWhere={selectAllWhere}
            />
          );
        },
      });
    }

    actions.push({
      id: "set-assignees",
      type: "popover",
      label: "Assignees",
      icon: "users",
      contentProps: { padding: "none", width: 384 },
      content: ({ selectedRows, selectAllGroups }) => {
        const selectAllWhere = buildSelectAllWhere(
          selectAllGroups,
          getGroupWhere,
        );
        return (
          <BulkAssigneeContent
            view={AnalyticsView.List}
            scope={scope.kind}
            bulkMutations={bulkMutations}
            selectedRows={selectedRows}
            selectAllWhere={selectAllWhere}
          />
        );
      },
    });

    return actions;
  }, [scope, workflowId, bulkMutations, availableStages, refetchGroups, getGroupWhere]);

  // --- Build DataTable groups ---
  const isGroupCountsReady = !isGrouped || groupCountsReady;
  const totalIssueCount = useMemo(() => {
    if (!isGrouped) return states.get("__all__")?.total ?? 0;
    return Object.values(groupCountsRecord).reduce(
      (sum, count) => sum + count,
      0,
    );
  }, [isGrouped, groupCountsRecord, states]);

  const hasOtherActiveFilters =
    Boolean(searchValue) ||
    Boolean(tableFilters.where) ||
    panelFilters.activeFilterCount > 0;

  const isTrulyEmpty = useMemo(() => {
    if (hasOtherActiveFilters || isCurrentUserFilterApplied) return false;
    if (!isGrouped && (states.get("__all__")?.loading ?? true)) return false;
    if (isGrouped && !isGroupCountsReady) return false;
    return totalIssueCount === 0;
  }, [
    hasOtherActiveFilters,
    isCurrentUserFilterApplied,
    isGroupCountsReady,
    isGrouped,
    states,
    totalIssueCount,
  ]);

  const isCurrentUserEmpty = useMemo(() => {
    if (!isCurrentUserFilterApplied || hasOtherActiveFilters) return false;
    if (!isGrouped && (states.get("__all__")?.loading ?? true)) return false;
    if (isGrouped && !isGroupCountsReady) return false;
    return totalIssueCount === 0;
  }, [
    hasOtherActiveFilters,
    isCurrentUserFilterApplied,
    isGroupCountsReady,
    isGrouped,
    states,
    totalIssueCount,
  ]);

  const groups = useMemo<DataTableGroupType<IssueRow>[]>(() => {
    if (isGrouped && !isGroupCountsReady) return [];
    if (!isGrouped) {
      return [
        {
          key: "__all__",
          label: "All Projects",
          totalCount: states.get("__all__")?.total,
          ...getGroupProps("__all__"),
        },
      ];
    }
    return visibleGroups.map((g) => {
      // If the group has been activated and has data from GroupQueryProvider, use it
      const stateData = states.get(g.key);
      if (activatedKeys.has(g.key) && stateData) {
        return {
          key: g.key,
          label: g.label,
          ...getGroupProps(g.key),
        };
      }
      // Placeholder — DataTable will wire up lazy activation via onGroupActivate
      return {
        key: g.key,
        label: g.label,
        data: [] as IssueRow[],
        placeholder: true,
        dataOffset: 0,
      };
    });
  }, [
    isGrouped,
    isGroupCountsReady,
    visibleGroups,
    states,
    getGroupProps,
    activatedKeys,
  ]);

  const stageById = useMemo(() => {
    const map = new Map<string, StageOption>();
    for (const s of availableStages) map.set(s.stageId, s);
    return map;
  }, [availableStages]);

  // "Automate stage" on a stage group — shared gate.
  const { canAutomateStage } = useAutomateStage(workflowId);
  // Inferred rather than annotated: a `typeof userPool.items` query inside the
  // callback reads as a closure over `userPool` to anything that does not
  // resolve types, the deps lint among them.
  const userById = useMemo(
    () => new Map(userPool.items.map((i) => [i.id, i] as const)),
    [userPool.items],
  );
  const teamById = useMemo(
    () => new Map(teamPool.items.map((i) => [i.id, i] as const)),
    [teamPool.items],
  );
  const issueTypeById = useMemo(() => {
    const map = new Map<string, (typeof issueTypes)[number]>();
    for (const t of issueTypes) map.set(t.issueTypeId, t);
    return map;
  }, [issueTypes]);

  // --- Group title renderer ---
  const renderGroupTitle = useCallback(
    (groupKey: string) => {
      if (groupKey === UNASSIGNED_GROUP_KEY) {
        const dimension = FIELD_TO_DIMENSION[groupByField];
        return (
          <Text size="sm" variant="muted">
            {dimension ? UNASSIGNED_DIMENSION_LABEL[dimension] : "Unassigned"}
          </Text>
        );
      }

      if (groupByField === "stage") {
        const stage = stageById.get(groupKey);
        if (!stage) return groupKey;
        return (
          <Box direction="row" align="center" gap="sm" overflow="hidden">
            <EntityTag
              entity={{ kind: "stage", id: stage.stageId, stage }}
              interactive={false}
            />
          </Box>
        );
      }

      if (groupByField === "assignees") {
        const user = userById.get(groupKey);
        if (user) {
          return (
            <Box direction="row" align="center" gap="sm" overflow="hidden">
              <EntityTag
                entity={{
                  kind: "user",
                  id: user.id,
                  user: { name: user.title, profileImage: user.avatar?.src ?? null },
                }}
                interactive={false}
              />
            </Box>
          );
        }
        return groupKey;
      }

      if (groupByField === "team") {
        const team = teamById.get(groupKey);
        if (team) {
          return (
            <Box direction="row" align="center" gap="sm" overflow="hidden">
              <EntityTag
                entity={{
                  kind: "team",
                  id: team.id,
                  team: {
                    teamName: team.title,
                    icon: {
                      icon: iconSourceToName(team.avatar?.icon),
                      color: team.avatar?.color ?? null,
                    },
                  },
                }}
                interactive={false}
              />
            </Box>
          );
        }
        return groupKey;
      }

      if (groupByField === "age") {
        const ageInfo = AGE_STATUS_CONFIG[groupKey as AgeStatus];
        if (ageInfo) {
          return (
            <Box direction="row" align="center" gap="sm" overflow="hidden">
              {/* The config's semantic `background` token doubles as the
                  icon color. */}
              <Icon name="clock" size="md" color={ageInfo.background} />
              <Box fill="flex" shrink>
                <Text size="sm" weight="medium" truncate={1}>
                  {ageInfo.label === "Disable age" ? "Disabled" : ageInfo.label}
                </Text>
              </Box>
            </Box>
          );
        }
        return groupKey;
      }

      if (groupByField === "issueType") {
        const it = issueTypeById.get(groupKey);
        if (!it) return groupKey;
        return (
          <Box direction="row" align="center" gap="sm" overflow="hidden">
            <EntityTag
              entity={{ kind: "issueType", id: it.issueTypeId, issueType: it }}
              size="md"
              interactive={false}
            />
          </Box>
        );
      }

      return groupKey;
    },
    [groupByField, stageById, userById, teamById, issueTypeById],
  );

  // Stable reference: groupActions feeds the memoized per-group sub-tables,
  // so an inline arrow here would re-render every group on every render.
  const renderGroupActions = useCallback(
    (ctx: DataTableGroupActionsContext<IssueRow>) =>
      renderDefaultGroupActions({
        ...ctx,
        // Only grouped by stage: the group key IS the stage id, so
        // offer "Automate stage" seeded with the workflow + this stage.
        append:
          canAutomateStage &&
          groupByField === "stage" &&
          workflowId &&
          stageById.get(ctx.groupKey) ? (
            <AutomateStageMenuItem
              workflowId={workflowId}
              stageId={ctx.groupKey}
              iconSize="lg"
            />
          ) : undefined,
      }),
    [canAutomateStage, groupByField, workflowId, stageById],
  );

  // Create needs a single workflow to add into.
  const handleAddProject = useMemo(
    () =>
      workflowId
        ? () => openCreateIssue({ workflowId, source: "list_view" })
        : undefined,
    [workflowId, openCreateIssue],
  );

  return (
    <>
      {/* Per-group query providers — only mounted for activated groups */}
      {isGrouped &&
        FIELD_TO_DIMENSION[groupByField] &&
        visibleGroups
          .filter((g) => activatedKeys.has(g.key))
          .map((g) => {
            const groupWhere = applyCurrentUserFilter(
              combineWhereConditions(
                [
                  groupBaseWhere,
                  groupWhereForField(groupByField, g.key),
                ].filter(Boolean) as Record<string, unknown>[],
              ),
              isCurrentUserFilterApplied ?? false,
            );
            return (
              <GroupQueryProvider<IssueRow>
                key={g.key}
                groupKey={g.key}
                document={IssuesDocument}
                variables={{ where: groupWhere, orderBy, limit: 50 }}
                getConnection={getConnection}
                onStateChange={registerState}
              />
            );
          })}

      {/* Ungrouped query provider — a single workflow co-fetches its stages via
          IssueListDataDocument; a cross-workflow scope reads plain Issues. */}
      {!isGrouped &&
        (workflowId ? (
          <GroupQueryProvider<IssueRow>
            groupKey="__all__"
            document={IssueListDataDocument}
            variables={{ where, orderBy, workflowId, limit: 50 }}
            getConnection={getConnection}
            onStateChange={registerState}
          />
        ) : (
          <GroupQueryProvider<IssueRow>
            groupKey="__all__"
            document={IssuesDocument}
            variables={{ where, orderBy, limit: 50 }}
            getConnection={getConnection}
            onStateChange={registerState}
          />
        ))}

      {/* Same toolbar chrome as the widget surfaces (surface-chrome.tsx):
          page-default background with a bottom hairline. */}
      <Box background="background">
        <DataTableToolbar>
          <DataTableToolbarLeft>
            <DataTableGroupByDropdown
              groupByField={groupByField}
              setGroupByField={(field) => {
                setGroupByField(field);
                track({
                  context: AnalyticsContext.Issue,
                  event: IssueEvent.ChangeGrouping,
                  data: { view: AnalyticsView.List, scope: scope.kind, dimension: field },
                });
              }}
              groupByLabels={groupByLabels}
              groupByIcons={groupByIcons}
              groupSortDirection={groupSortDirection}
              setGroupSortDirection={setGroupSortDirection}
            />
          </DataTableToolbarLeft>
          <DataTableToolbarActions>
            <CurrentUserButton
              selected={isCurrentUserButtonSelected}
              onClick={() => {
                handleCurrentUserPress();
                track({
                  context: AnalyticsContext.Issue,
                  event: IssueEvent.ToggleOnlyMe,
                  data: { view: AnalyticsView.List, scope: scope.kind, enabled: true },
                });
              }}
              onClearClick={() => {
                handleCurrentUserClear();
                track({
                  context: AnalyticsContext.Issue,
                  event: IssueEvent.ToggleOnlyMe,
                  data: { view: AnalyticsView.List, scope: scope.kind, enabled: false },
                });
              }}
              size="xs"
            />
            <FilterToggle
              size="xs"
              icon="filter"
              count={panelFilters.activeFilterCount}
              onClick={toggleFilterPanel}
              onClear={panelFilters.clearAll}
            />
            <ExpandableSearch
              onValueChange={setSearchValue}
              placeholder="Search projects..."
              size="xs"
            />
            <DataTableColumnSettings
              columns={columns}
              pinnedColumnIds={ISSUES_PINNED_COLUMN_IDS}
              {...tableView.settingsProps}
            />
            {handleAddProject && (
              <Box direction="row" align="center" gap="sm">
                
                <Separator orientation="vertical" className="h-4" />
                <ToolbarAction icon="plus" label="Add project" onClick={handleAddProject} />
              </Box>
            )}
          </DataTableToolbarActions>
        </DataTableToolbar>
        <Separator />
      </Box>

      <Box direction="column" fill="flex">
        {isTrulyEmpty ? (
          <Box fill="flex" align="center" justify="center">
            <DataTableEmpty
              title="No projects yet"
              description="Create your first project to get started"
              media={<Icon name="list" size="2xl" color="muted" />}
              action={
                handleAddProject ? (
                  <Button size="sm" onClick={handleAddProject}>
                    <Icon name="plus" size="md" />
                    <Text size="sm">Add project</Text>
                  </Button>
                ) : undefined
              }
            />
          </Box>
        ) : isCurrentUserEmpty ? (
          <Box fill="flex" align="center" justify="center">
            <DataTableEmpty
              title="No projects assigned to you"
              description="You don't have any projects in this view"
              media={<Icon name="user" size="2xl" color="muted" />}
              action={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCurrentUserClear}
                >
                  <Text size="sm">Turn off only me</Text>
                </Button>
              }
            />
          </Box>
        ) : (
          <DataTable
            key={scopeKey(scope)}
            columns={columns}
            groups={groups}
            getRowId={(r) => r.issueId}
            sorting={sorting}
            onSortingChange={setSorting}
            stickyHeader
            density="regular"
            loading={
              groups.length === 1 &&
              at(groups, 0, "group").data.length === 0 &&
              (states.get(at(groups, 0, "group").key)?.loading ?? false)
            }
            pinnedColumnIds={ISSUES_PINNED_COLUMN_IDS}
            fillColumnId="name"
            {...tableView.tableProps}
            paddingX="md"
            lazyGroups={isGrouped}
            onGroupActivate={handleGroupActivate}
            groupCounts={isGrouped ? groupCountsRecord : undefined}
            groupTitle={isGrouped ? renderGroupTitle : undefined}
            groupActions={renderGroupActions}
            bulkActions={bulkActions}
            canSelectRow={(issue) => canEdit(issue.viewerAccessLevel)}
            renderRowActions={renderRowActions}
            emptyState={
              <DataTableEmpty
                message={
                  searchValue
                    ? "No projects match your search"
                    : "No projects found"
                }
              />
            }
          />
        )}
      </Box>

      {ageDialogIssue && (
        <UpdateAgeDialog
          open
          onOpenChange={handleAgeDialogClose}
          issueId={ageDialogIssue.issueId}
          age={ageDialogIssue.age}
          stageId={ageDialogIssue.stageId}
        />
      )}

      <RenameIssueDialog
        open={!!renameTarget}
        onOpenChange={(open) => { if (!open) setRenameTarget(null); }}
        value={renameValue}
        onValueChange={setRenameValue}
        onConfirm={() => {
          if (renameTarget) renameIssue(renameTarget.issueId, renameValue);
          setRenameTarget(null);
        }}
      />
    </>
  );
}
